import React from 'react';
import { motion } from 'framer-motion';
import { 
    FaCode, FaServer, FaDatabase, FaTools, FaPalette, 
    FaHtml5, FaCss3Alt, FaJs, FaReact, FaNodeJs, FaGitAlt,
    FaGithub, FaWindows, FaPython, FaPaintBrush, FaCloud,
    FaMobileAlt, FaCheckCircle, FaBolt, FaGamepad,
    FaHandshake, FaChartLine, FaFileSignature, FaUsers, FaComments, FaGlobeAmericas,
    FaProjectDiagram, FaClipboardList, FaCalendarAlt, FaListUl, FaUserTie, FaLifeRing
} from 'react-icons/fa';

const skillCategories = [
    {
        title: "Business Development",
        icon: <FaHandshake className="text-pink-600 text-2xl" />,
        skills: [
            { name: "New Markets & Partnerships", icon: <FaChartLine className="text-pink-500" /> },
            { name: "Conferences & Networking", icon: <FaGlobeAmericas className="text-olive-500" /> },
            { name: "Client Meetings & Relationships", icon: <FaComments className="text-gray-700" /> },
            { name: "Proposals & Statements of Work", icon: <FaFileSignature className="text-pink-500" /> },
            { name: "Technical Consultancy", icon: <FaUserTie className="text-olive-500" /> }
        ]
    },
    {
        title: "Project Management",
        icon: <FaProjectDiagram className="text-pink-600 text-2xl" />,
        skills: [
            { name: "Planning & Scheduling", icon: <FaCalendarAlt className="text-pink-500" /> },
            { name: "Requirements & User Stories", icon: <FaListUl className="text-olive-500" /> },
            { name: "Stakeholder Coordination", icon: <FaUsers className="text-gray-700" /> },
            { name: "Delivery & Quality", icon: <FaClipboardList className="text-pink-500" /> },
            { name: "Post-Delivery Support", icon: <FaLifeRing className="text-olive-500" /> }
        ]
    },
    {
        title: "Backend & Programming",
        icon: <FaServer className="text-pink-600 text-2xl" />,
        skills: [
            { name: "C#", icon: <FaCode className="text-pink-500" />, level: 90 },
            { name: ".NET", icon: <FaWindows className="text-olive-500" />, level: 85 },
            { name: "Node.js", icon: <FaNodeJs className="text-gray-700" />, level: 80 },
            { name: "Python", icon: <FaPython className="text-pink-500" />, level: 75 },
            { name: "REST APIs", icon: <FaCloud className="text-olive-500" />, level: 85 },
            { name: "OOP", icon: <FaBolt className="text-gray-700" />, level: 90 }
        ]
    },
    {
        title: "Front End",
        icon: <FaCode className="text-pink-600 text-2xl" />,
        skills: [
            { name: "HTML", icon: <FaHtml5 className="text-pink-500" />, level: 95 },
            { name: "CSS", icon: <FaCss3Alt className="text-olive-500" />, level: 90 },
            { name: "JavaScript", icon: <FaJs className="text-gray-700" />, level: 85 },
            { name: "React", icon: <FaReact className="text-pink-500" />, level: 80 },
            { name: "Flutter", icon: <FaMobileAlt className="text-olive-500" />, level: 70 }
        ]
    },
    {
        title: "Tools & Platforms",
        icon: <FaTools className="text-pink-600 text-2xl" />,
        skills: [
            { name: "Git", icon: <FaGitAlt className="text-gray-700" />, level: 85 },
            { name: "GitHub", icon: <FaGithub className="text-pink-500" />, level: 90 },
            { name: "Postman", icon: <FaCloud className="text-olive-500" />, level: 80 },
            { name: "Appian", icon: <FaTools className="text-gray-700" />, level: 75 },
            { name: "Linux", icon: <FaServer className="text-pink-500" />, level: 75 },

        ]
    },
    {
        title: "Databases",
        icon: <FaDatabase className="text-pink-600 text-2xl" />,
        skills: [
            { name: "SQL Server", icon: <FaDatabase className="text-olive-500" />, level: 85 },
            { name: "MongoDB", icon: <FaDatabase className="text-gray-700" />, level: 75 },
            { name: "Database Design", icon: <FaCheckCircle className="text-pink-500" />, level: 80 },
            { name: "PHPMyAdmin", icon: <FaDatabase className="text-olive-500" />, level: 70 }
        ]
    },
    {
        title: "Game Development",
        icon: <FaGamepad className="text-pink-600 text-2xl" />,
        skills: [
            { name: "Unity", icon: <FaGamepad className="text-gray-700" />, level: 80 },
            { name: "Game Physics", icon: <FaBolt className="text-pink-500" />, level: 70 },
            { name: "C# for Games", icon: <FaCode className="text-olive-500" />, level: 85 }
        ]
    },
    {
        title: "Design",
        icon: <FaPalette className="text-pink-600 text-2xl" />,
        skills: [
            { name: "Figma", icon: <FaPaintBrush className="text-gray-700" />, level: 85 },
            { name: "Canva", icon: <FaPalette className="text-pink-500" />, level: 90 }
        ]
    },
];

const Skills = () => {
    return (
        <section id="skills" className="py-20 bg-cream">
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="mb-12 text-center"
                >
                    <h2 className="text-4xl font-bold text-gray-800 mb-4">Professional Skills</h2>
                    <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                    Bringing in the work, running the project, and understanding the technology behind it
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-rose-500 mx-auto mt-4"></div>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {skillCategories.map((category, catIndex) => (
                        <motion.div
                            key={catIndex}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: catIndex * 0.1 }}
                            viewport={{ once: true }}
                            className="bg-gradient-to-br from-white to-pink-50 rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-pink-100"
                        >
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mr-4">
                                    {category.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800">{category.title}</h3>
                            </div>
                            
                            <div className="space-y-4">
                                {category.skills.map((skill, skillIndex) => (
                                    <div key={skillIndex} className="mb-3">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center mr-2 shadow-sm">
                                                    {skill.icon}
                                                </div>
                                                <span className="font-medium text-gray-700">{skill.name}</span>
                                            </div>
                                        </div>
                                       
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Skills;